<script setup lang="ts">
import {nextTick, onBeforeMount, ref, watch} from "vue";
import Drawer from 'primevue/drawer';
import {
  drawerVisible,
  judgmentOptions,
  loadKdm,
  kdm,
  globalError,
  bsModalState,
  discussionState,
  markCommentsAsRead,
  updateCommentSummary,
  clearCommentSummary
} from "@/store/kdm";
import SpinnerIcon from "@/icons/SpinnerIcon.vue";
import {loadKrr} from "@/store/krr";
import ConfirmDialog from 'primevue/confirmdialog';
import KDMrawerContent from '@/kdm/KDMDrawerContent.vue'
import KdmMainContent from "@/kdm/KdmMainContent.vue";
import DiscussionPanel from "@/components/DiscussionPanel.vue";
import {useBootstrapModalListeners} from "@/composables/useBootstrapModalListeners";

const {initModalEventListeners, preventSidebarDuplicateOnPC} = useBootstrapModalListeners()

const gettingReady = ref(false)
const title = document.querySelector('h1')?.textContent as string
let cssJudgmentIds: string[] = []

// After a Clear in the discussion panel, reset the affected comment counts.
function onCommentsCleared(payload: { coords: string, all: boolean }): void {
  if (payload.all) {
    clearCommentSummary();
  } else {
    clearCommentSummary(payload.coords);
  }
}

function syncJudgmentCssVariables(): void {
  const nextIds: string[] = []
  judgmentOptions.value.forEach((option, id) => {
    nextIds.push(id)
    document.documentElement.style.setProperty(`--judgment-option-${id}`, option.color)
  })
  cssJudgmentIds.forEach(id => {
    if (!nextIds.includes(id)) {
      document.documentElement.style.removeProperty(`--judgment-option-${id}`)
    }
  })
  cssJudgmentIds = nextIds
}

onBeforeMount(async () => {
  gettingReady.value = true
  const nid = (window as any).drupalSettings?.epbKdmId || document.querySelector('article')?.getAttribute('data-history-node-id') as string
  await loadKdm(nid)
  syncJudgmentCssVariables()
  watch(judgmentOptions, () => {
    syncJudgmentCssVariables()
  }, {deep: true})

  if (kdm.value && kdm.value.krrId) {
    await loadKrr(kdm.value.krrId)
  } else if (kdm.value?.kdm_type === 3) {
    console.warn("No KRR ID associated with this KDM")
  }
  gettingReady.value = false
  await nextTick(() => {
    initModalEventListeners('kdmFullScreenModal', bsModalState)
    preventSidebarDuplicateOnPC(bsModalState)
  })
})
</script>

<template>
  <div v-if="gettingReady" class="dm__getting-ready">
    <SpinnerIcon/>
  </div>
  <template v-else>
    <div ref="modal" class="modal fade hidden" id="kdmFullScreenModal" tabindex="-1"
         aria-labelledby="kdmFullScreenModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-fullscreen">
        <div class="modal-content d-flex flex-column" style="height: 100vh;">
          <div class="modal-header pe-4 flex-shrink-0">
            <h1 class="modal-title fs-4" id="kdmFullScreenModalLabel">{{ title }}</h1>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="p-4 bg-white d-flex flex-column" style="flex: 1 1 auto; min-height: 0; overflow: hidden;">
            <KdmMainContent v-if="bsModalState"/>
          </div>
          <div class="modal-footer pe-4 bg-white flex-shrink-0">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="globalError" class="alert alert-danger m-4">
        {{ globalError }}
    </div>
    <!-- Removed obsolete Fuddle alert for Type 1 -->
    <KdmMainContent v-if="!bsModalState && kdm?.kdm_type"/>
    <div v-if="kdm && !kdm.krrId && kdm.kdm_type === 3 && !globalError" class="alert alert-warning m-4">
        Attention : Aucun KRR n'est associé à ce KDM (Type 3). Veuillez vérifier les crosslinks du nœud.
    </div>
    <Drawer v-model:visible="drawerVisible" header="Available Arguments" position="right">
      <KDMrawerContent/>
    </Drawer>

    <Drawer v-model:visible="discussionState.visible" position="right" class="discussion-drawer" :style="{ width: '400px' }" @hide="markCommentsAsRead(discussionState.coords)">
      <template #header>
        <span class="fw-bold">Discussion</span>
      </template>
      <DiscussionPanel
        v-if="discussionState.visible"
        :node-id="discussionState.nodeId"
        :coords="discussionState.coords"
        :can-write="discussionState.canWrite"
        :can-clear="discussionState.canClear"
        @close="discussionState.visible = false"
        @comment-added="updateCommentSummary"
        @cleared="onCommentsCleared"
      />
    </Drawer>

    <ConfirmDialog/>
  </template>
</template>

<style>
/* Overwrite PrimeVue variables */
.p-drawer-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
</style>

<style scoped>
.dm__getting-ready {
  display: grid;
  place-items: center;
  min-height: 500px;
}

:deep(.p-tabpanel) {
  margin-top: -1rem;
}
</style>
